import { Box, Card, Container } from '@mui/material';
import { capitalCase } from 'change-case';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { useBinary, useEpisodeOfCares } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { RejectionFiles, UploadAvatar } from 'src/components/upload';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { BannerInfoName } from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import {
  BannerInfoComponents,
  EOCBannerComponent,
  InfoComponent,
  PatientBasicInfo,
} from 'src/careflow/care-coordination/patient/patient-banner/PatientBasicInfo';
import IcomxInlineTelecomAction from 'src/sections/crs/patient/components/icomx/IcomxInlineTelecomAction';
import { getEOCLastByCode, getFhirIdFromEntity } from 'src/utils/fhir';

type FileRejectionByPatient = {
  [key: string]: FileRejection[];
};

export function WorkflowHeader({
  patient,
  configurations,
  i18n,
  sx,
}: {
  patient: WrappedPatient | null;
  configurations: any;
  i18n: i18nFunction;
  sx?: any;
}) {
  const [fileimg, setfileImg] = useState('');
  const [fileRejectionsByPatient, setFileRejectionsByPatient] =
    useState<FileRejectionByPatient | null>({});
  const [binary] = useBinary(getFhirIdFromEntity(patient?.photo?.[0].url || ''));
  const [, { find: findEpisodeOfCare }] = useEpisodeOfCares({ autofetch: false });
  const [episodeOfCare, setEpisodeOfCare] = useState<string[]>([]);

  const getEpisodeOfCare = useCallback(async () => {
    if (!patient) return;

    const episodeOfCarePatient = await findEpisodeOfCare({ patient: patient?.id });
    const mapEOC = getEOCLastByCode(episodeOfCarePatient);
    if (mapEOC && mapEOC?.length > 0) {
      setEpisodeOfCare(mapEOC);
    }
  }, [findEpisodeOfCare, patient]);

  useEffect(() => {
    setfileImg('');
    if (binary?.data) {
      setfileImg(`data:${binary?.contentType};base64,${binary?.data}`);
    }
  }, [patient, binary]);

  useEffect(() => {
    getEpisodeOfCare();
  }, [getEpisodeOfCare]);

  const handlFileRejections = useCallback(
    (fileRejections: FileRejection[]) => {
      if (!patient?.id) return;
      const patientId = patient?.id;
      setFileRejectionsByPatient((currFileRejectionsByPatient) => ({
        ...currFileRejectionsByPatient,
        [patientId]: fileRejections,
      }));
    },
    [patient]
  );

  const bannerInfoComponents: BannerInfoComponents = useMemo(
    () => ({
      [BannerInfoName.MRN]: () => (
        <InfoComponent
          label={i18n('patients.mrn', 'crs')}
          value={patient?.getMRN(configurations?.mpi?.systemMrn)?.value}
          key={'bannedInfoNameMRN'}
        />
      ),
      [BannerInfoName.PRIMARY_EMAIL]: () => (
        <InfoComponent
          label={i18n('patients.email', 'crs')}
          value={patient?.getPrimaryEmail()?.value}
          endAdornment={
            <IcomxInlineTelecomAction
              resourceType="Patient"
              resourceId={patient?.id}
              system="email"
              tooltip="Open patient email in iComx"
              value={patient?.getPrimaryEmail()?.value}
            />
          }
          key={'bannedInfoNamePrimaryEmail'}
        />
      ),
      [BannerInfoName.PRIMARY_PHONE]: () => (
        <InfoComponent
          label={i18n('patients.phone', 'crs')}
          value={patient?.getPrimaryPhone()?.value}
          endAdornment={
            <IcomxInlineTelecomAction
              resourceType="Patient"
              resourceId={patient?.id}
              system="phone"
              tooltip="Open patient phone in iComx"
              value={patient?.getPrimaryPhone()?.value}
            />
          }
          key={'bannedInfoNamePrimaryPhone'}
        />
      ),
      [BannerInfoName.BIRTHDAY_AGE]: () => (
        <InfoComponent
          label={i18n('patients.dateOfBirth', 'crs')}
          value={`${patient?.getBirthDateForDisplay()} (${patient?.getAgeInYears()} years)`}
          key={'bannedInfoNameDOB'}
        />
      ),
      [BannerInfoName.AGE_GROUP]: () => (
        <InfoComponent
          label={i18n('patients.details.personalDetails.ageGroup', 'crs')}
          value={`${capitalCase(patient?.getAgeGroup() ?? '')}`}
          key={'bannedInfoNameAgeGroup'}
        />
      ),
      [BannerInfoName.MANAGING_ORG]: () => (
        <InfoComponent
          label={i18n('patients.details.personalDetails.organization', 'crs')}
          value={patient?.getOrganization()}
          key={'bannedInfoNameOrganization'}
        />
      ),
      [BannerInfoName.EOC]: () =>
        episodeOfCare ? <EOCBannerComponent episodeOfCare={episodeOfCare} i18n={i18n} /> : null,
    }),
    [patient, configurations, episodeOfCare, i18n]
  );

  return (
    <Container disableGutters maxWidth="xl">
      <Card
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          ...sx,
        }}
      >
        <Box
          sx={{
            my: 2,
            display: 'grid',
            gridTemplateColumns: '1.6fr 10.4fr ',
            alignItems: 'center',
          }}
        >
          <Box>
            <UploadAvatar
              file={fileimg.toString()}
              patient={patient}
              sx={{ border: `1px solid #008ECC`, height: 80, width: 80 }}
              onFileRejections={handlFileRejections}
              showFileRejections={false}
            />
          </Box>
          <Box>
            {patient && bannerInfoComponents && (
              <PatientBasicInfo
                patient={patient}
                showGoToProfile={true}
                bannerConfigExternal={bannerInfoComponents}
              />
            )}
          </Box>
        </Box>
        {!!patient?.id && !!fileRejectionsByPatient?.[patient?.id]?.length && (
          <Box sx={{ marginX: 2, marginBottom: 1, marginTop: -3 }}>
            <RejectionFiles
              variant="Alert"
              onClose={() => {
                handlFileRejections([]);
              }}
              fileRejections={[]}
            />{' '}
          </Box>
        )}
      </Card>
    </Container>
  );
}
